import { computed } from 'vue';

import { SOURCE_STATUS_MAP } from '@/constants/data_sources';
import { MDM_TYPES } from '@/constants/mdm';
import { useSourcesStore } from '@/stores';
import type { Source, SourceField } from '@/types/sources';

const [DELETING_STATUS] = SOURCE_STATUS_MAP.deleting;
const [UNAUTHORIZED_STATUS] = SOURCE_STATUS_MAP.missing;

const HAS_DISAPPEARED_REASON = 'has_disappeared';

export function useAccountProductSource(feedId: () => number | undefined) {
  const sourcesStore = useSourcesStore();

  const accountProductSource = computed<Source | undefined>(() => {
    const id = feedId();
    if (id == null) return undefined;
    return sourcesStore
      .getSourcesByFeedId(id)
      .find(
        (source) =>
          source.mdm_type === MDM_TYPES.ACCOUNT_PRODUCT &&
          source.status !== DELETING_STATUS,
      );
  });

  const hasSyncedAccountSource = computed<boolean>(() => {
    const id = feedId();
    if (id == null) return false;
    return sourcesStore
      .getSourcesByFeedId(id)
      .some(
        (source) =>
          source.mdm_type === MDM_TYPES.ACCOUNT &&
          source.status !== DELETING_STATUS &&
          source.status !== UNAUTHORIZED_STATUS,
      );
  });

  const fieldsByMdmProperty = computed<Record<string, SourceField>>(() => {
    const fields = accountProductSource.value?.fields ?? [];
    return Object.fromEntries(
      fields
        .filter((field) => Boolean(field.mdm_property))
        .map((field) => [field.mdm_property, field]),
    );
  });

  const lockedColumns = computed<string[]>(() =>
    Object.values(fieldsByMdmProperty.value).map((field) => field.column),
  );

  const disappearedMdmProperties = computed<Set<string>>(
    () =>
      new Set(
        Object.entries(fieldsByMdmProperty.value)
          .filter(
            ([, field]) =>
              field.syncing_disabled_reason === HAS_DISAPPEARED_REASON,
          )
          .map(([mdmProperty]) => mdmProperty),
      ),
  );

  return {
    accountProductSource,
    fieldsByMdmProperty,
    lockedColumns,
    hasSyncedAccountSource,
    disappearedMdmProperties,
  };
}
